import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const isSuccessfulStatus = (status) => status >= 200 && status < 300;

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken') || null);
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken') || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('accessToken'));
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [loading, setLoading] = useState(true);

  const baseURL = process.env.REACT_APP_BASEURL;

  useEffect(() => {
    const validateTokenAndFetchUser = async () => {
      if (!accessToken) {
        console.log('No access token found in localStorage');
        setIsAuthenticated(false);
        setIsAdmin(false);
        setIsSuperuser(false);
        setLoading(false);
        return;
      }

      try {
        // Validate token
        const tokenResponse = await axios.post(
          `${baseURL}/user-login/verify`,
          { token: accessToken },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!isSuccessfulStatus(tokenResponse.status)) {
          throw new Error(`Token validation failed with status ${tokenResponse.status}`);
        }

        // Fetch user details
        const userResponse = await axios.get(`${baseURL}/users`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userList = userResponse.data || [];

        // For non-Admins, /users returns only the current user's details
        // For Admins, it returns a list, so we need to identify the current user
        let currentUser = null;
        if (userList.length === 1) {
          // Non-Admin case: single user (current user)
          currentUser = userList[0];
        } else {
          // Admin case: find user with is_admin: true or fallback to first user
          currentUser = userList.find((user) => user.is_admin) || userList[0];
        }

        if (!currentUser) {
          throw new Error('No user found in /users response');
        }

        setIsAdmin(currentUser.is_admin === true);
        setIsSuperuser(currentUser.is_superuser === true);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Token validation or user fetch error:', error.response?.status, error.response?.data || error.message);
        setAccessToken(null);
        setRefreshToken(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        setIsSuperuser(false);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setLoading(false);
      }
    };
    validateTokenAndFetchUser();
  }, [accessToken]);

  useEffect(() => {
    let interval;
    if (isAuthenticated && refreshToken) {
      interval = setInterval(async () => {
        try {
          const res = await axios.post(
            `${baseURL}/user-login/refresh`,
            { refresh: refreshToken },
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          if (!isSuccessfulStatus(res.status)) {
            throw new Error(`Token refresh failed with status ${res.status}`);
          }
          const { access, refresh: newRefresh } = res.data;
          setAccessToken(access);
          setRefreshToken(newRefresh);
          localStorage.setItem('accessToken', access);
          localStorage.setItem('refreshToken', newRefresh);
        } catch (error) {
          console.error('Token refresh error:', error.response?.status, error.response?.data || error.message);
          if (error.response?.status === 401) {
            setAccessToken(null);
            setRefreshToken(null);
            setIsAuthenticated(false);
            setIsAdmin(false);
            setIsSuperuser(false);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
          }
        }
      }, 600000); // 10 minutes
    }
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshToken, accessToken]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        refreshToken,
        isAuthenticated,
        isAdmin,
        isSuperuser,
        setAccessToken,
        setRefreshToken,
        setIsAuthenticated,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
